import React from 'react';
import { Filter } from '../../../store/mockServices/mockServiceTypes';
import MockServiceStatusFilter from '../MockServicesFilter/MockServiceStatusFilter';
import _ from 'lodash';
import BlazeInputDropdown, {
  BlazeDropdownItem,
} from '../../../components/ui/dropdown/BlazeDropdown/BlazeInputDropdown';
import { createCraneLocationsDropDownItemsByFuncIds } from '../../../utils/locationUtil';
import RefreshLocationsBtn from '../../../components/refreshLocations/RefreshLocationsBtn';
import ServicePicker from '../../../components/ui/servicePicker/ServicePicker';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store/rootReducer';
import TagTextFilter, { SearchContext } from '../../../components/ui/filters/TagTextFilter';
import { setPageFilter } from '../../../store/ui/pageFilters/pageFilterActions';
import { MockServiceStatus } from '../../../types/rest/mockServiceTypes';
import { Service, TransactionType } from '../../../types/rest/assetCatalogTypes';
import { PROVIDER_OPL } from '../../../constants/bzm-constants';
import { GROUP_CLOUD_LOCATIONS } from '../../../types/rest/locationTypes';
import { resolvePlaceholder } from '../Tabs/MockServices/Locations';
import { getPrivateLocationConfigUrl, isFeatureEnabled, isRoleWithPermissions } from '../../../utils/bzmUserConfigUtil';
import TypeFilter, { TypeFilterSelection } from 'components/ui/filters/TypeFilter/TypeFilter';

export const defaultLocationOption: BlazeDropdownItem = { id: '0', name: '- All Locations -' };

interface FilterProps {
  filter: Filter;
  onChange: (filter: Filter, source?: 'location' | 'service' | 'search' | 'status' | 'type') => void;
  /** Enables / Disables filters based on config */
  config: {
    locationFilter: boolean;
    serviceFilter: boolean;
    statusFilter: boolean;
    searchFilter: boolean;
    typeFilter?: boolean;
  };
  /** Fills in verbiage based on context*/
  searchContext?: SearchContext;

  className?: string;

  /** When service changes */
  onServiceChanged?: () => void;

  /** Show only some type of locations */
  locationsFilterBy?: typeof GROUP_CLOUD_LOCATIONS | typeof PROVIDER_OPL;

  /**
   * Service filter is stored in redux and used across different screens. If this is set to false,
   * the service id will be just passed through local filter and the global one is not going to be updated.
   */
  isGlobalServiceFilter?: boolean;

  /**
   * When true, service picker will not provide option to add new service.
   */
  disableAddService?: boolean;

  /**
   * When true, service picker will not provide option to clone.
   */
  isCloneEnabled?: boolean;
}

/**
 * Configurable filter with Service, Location and Status Dropdown.
 * @param props
 * @constructor
 */
const FilterBar = (props: FilterProps) => {
  const locations = useSelector((state: ApplicationState) => state.mockservices.locData);
  let locationsItems = createCraneLocationsDropDownItemsByFuncIds(locations);
  if (props.locationsFilterBy !== undefined) {
    locationsItems = locationsItems.filter(l => l.group === props.locationsFilterBy);
  }
  const pageFilter = useSelector((state: ApplicationState) => state.ui.pageFilter);
  const blazeUserConfig = useSelector((state: ApplicationState) => state.blazeUserConfig.blazeUserConfig);
  const dispatch = useDispatch();

  const onServiceChangedHandler = (service: Service) => {
    if (_.isNil(props.isGlobalServiceFilter) || props.isGlobalServiceFilter === true) {
      const pageFilterUpdate = _.cloneDeep(pageFilter);
      if (service) {
        pageFilterUpdate.serviceId = service.id;
      } else {
        pageFilterUpdate.serviceId = -1;
      }
      dispatch(setPageFilter(pageFilterUpdate));
    } else {
      const updatedFilter = _.cloneDeep(props.filter);
      updatedFilter.serviceId = service ? service.id : null;
      props.onChange(updatedFilter, 'service');
    }

    if (props.onServiceChanged) {
      props.onServiceChanged();
    }
  };

  const getServicePickerDropdown = () => {
    return (
      <>
        <label className="mr-3 font-weight-bold" htmlFor="serviceFilter" aria-describedby="servicePickerDiv">
          Service:
        </label>
        <div id="servicePickerDiv" className="mr-4 d-inline-block" style={{ minWidth: '200px' }}>
          <ServicePicker
            selectHandler={onServiceChangedHandler}
            isSelectAll={true}
            preSelected={
              props.filter.serviceId !== 0 && props.filter.serviceId !== -1 ? props.filter.serviceId : undefined
            }
            isCreateEnabled={_.isNil(props.disableAddService) ? true : !props.disableAddService}
            id="serviceFilter"
            isMaintenanceEnabled={isRoleWithPermissions(blazeUserConfig)}
            isCloneEnabled={_.isNil(props.isCloneEnabled) ? false : !props.isCloneEnabled}
          />
        </div>
      </>
    );
  };

  const locationFooter = (
    <div className={'text-center'}>
      <RefreshLocationsBtn variant={'button'} />
    </div>
  );

  const getLocationDropdown = () => (
    <>
      <label className="mr-3" htmlFor="qa-mock-service-location-picker">
        Location:
      </label>
      <div className="d-inline-block mr-4" style={{ minWidth: '200px' }}>
        <BlazeInputDropdown
          id={'qa-mock-service-location-picker'}
          showGroups={true}
          minDropdownMenuWidth={290}
          maxDropdownHeight={250}
          height={locationsItems.length > 0 ? undefined : 150}
          items={locationsItems}
          selected={props.filter.location}
          onClick={item => {
            const updatedFilter = _.cloneDeep(props.filter);
            if (item.id === '0') {
              updatedFilter.location = null;
              updatedFilter.shipId = null;
            } else {
              updatedFilter.location = item;
              updatedFilter.shipId = String(item.id);
            }
            props.onChange(updatedFilter);
          }}
          searchPlaceholder={'Search Locations...'}
          noItemsPlaceholder={resolvePlaceholder(locationsItems.length, getPrivateLocationConfigUrl())}
          isDefaultOptionSelectable={true}
          defaultOption={defaultLocationOption}
          dropDownFooter={locationFooter}
        />
      </div>
    </>
  );

  const updateTypeFilterHandler = (value: TransactionType) => {
    const updatedFilter = _.cloneDeep(props.filter);
    if (_.isNil(updatedFilter.types)) {
      updatedFilter.types = [value];
    } else {
      if (_.includes(updatedFilter.types, value)) {
        updatedFilter.types = _.filter(updatedFilter.types, item => item !== value);
      } else {
        updatedFilter.types.push(value);
      }
    }
    props.onChange(updatedFilter);
  };

  const getFilterSelection = (): TypeFilterSelection => {
    const isHttpEnabled = _.includes(props.filter.types, 'HTTP');
    const isMessagingEnabled = _.includes(props.filter.types, 'MESSAGING');
    return {
      http: isHttpEnabled,
      messaging: isMessagingEnabled,
    };
  };

  const getTypeFilter = () => {
    return (
      <div className={'d-inline-block ml-4'}>
        <TypeFilter handleChange={updateTypeFilterHandler} selected={getFilterSelection()} />
      </div>
    );
  };

  const getSearch = () => {
    return (
      <div className={'float-right'} style={{ display: 'inline-block', maxWidth: '200px' }}>
        <TagTextFilter
          className={'ml-auto'}
          searchTerm={props.filter.search}
          setSearchTerm={searchTerm => {
            const updatedFilter = _.cloneDeep(props.filter);
            updatedFilter.search = searchTerm;
            props.onChange(updatedFilter, 'search');
          }}
          context={props.searchContext}
        />
      </div>
    );
  };

  const getStatusDropdown = () => (
    <MockServiceStatusFilter
      status={props.filter.status}
      changeStatusHandler={(status: MockServiceStatus | null) => {
        const updatedFilter = _.cloneDeep(props.filter);
        updatedFilter.status = status;
        props.onChange(updatedFilter);
      }}
    />
  );

  return (
    <div className={props.className}>
      {props.config.serviceFilter && getServicePickerDropdown()}
      {props.config.locationFilter && getLocationDropdown()}
      {props.config.statusFilter && getStatusDropdown()}
      {props.config.searchFilter && getSearch()}
      {props.config.typeFilter && getTypeFilter()}
      <hr className={'mt-6'} />
    </div>
  );
};

export default FilterBar;
